<template>
  <div>
    <b-card class="mb-3">
      <h5>Connector Settings</h5>
      <b-form @submit.prevent="updateConnector">
        <b-row>
          <b-col md="6">
            <b-form-group label="Name *">
              <b-form-input v-model="form.name" required />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Connection Mode">
              <b-form-select v-model="form.connection_mode" :options="connectionModes" />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="Description">
              <b-form-textarea v-model="form.description" rows="2" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Rate Limit (per minute)">
              <b-form-input v-model.number="form.rate_limit_per_minute" type="number" min="1" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Rate Limit (per hour)">
              <b-form-input v-model.number="form.rate_limit_per_hour" type="number" min="1" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Rate Limit (per day)">
              <b-form-input v-model.number="form.rate_limit_per_day" type="number" min="1" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-button type="submit" variant="primary" :disabled="saving">
          <b-spinner v-if="saving" small class="mr-1" />
          Save Changes
        </b-button>
      </b-form>
    </b-card>

    <!-- Credentials -->
    <b-card class="mb-3">
      <h5>Credentials</h5>
      <p class="text-muted">Rotate the API key and secret. The new secret will only be shown once.</p>
      <div v-if="newCredentials">
        <b-alert show variant="warning">
          <strong>Save these credentials now — the secret will not be shown again.</strong>
        </b-alert>
        <b-row>
          <b-col md="6">
            <b-form-group label="API Key">
              <b-input-group>
                <b-form-input :value="newCredentials.api_key" readonly />
                <b-input-group-append>
                  <b-button variant="outline-secondary" @click="copyToClipboard(newCredentials.api_key)">
                    <i class="fa fa-copy" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="API Secret">
              <b-input-group>
                <b-form-input :value="newCredentials.api_secret" readonly />
                <b-input-group-append>
                  <b-button variant="outline-secondary" @click="copyToClipboard(newCredentials.api_secret)">
                    <i class="fa fa-copy" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>
      </div>
      <b-button variant="warning" :disabled="rotating" @click="showRotateModal = true">
        <b-spinner v-if="rotating" small class="mr-1" />
        Rotate Credentials
      </b-button>
    </b-card>

    <!-- Danger Zone -->
    <b-card border-variant="danger">
      <h5 class="text-danger">Danger Zone</h5>
      <p class="text-muted">Permanently delete this connector and all associated data. This cannot be undone.</p>
      <b-button variant="danger" @click="showDeleteModal = true">
        Delete Connector
      </b-button>
    </b-card>

    <!-- Rotate Confirm -->
    <b-modal v-model="showRotateModal" title="Rotate Credentials" @ok="rotateCredentials">
      <p>This will invalidate the current API key and secret. The connected plugin will stop working until updated with the new credentials.</p>
    </b-modal>

    <!-- Delete Confirm -->
    <b-modal v-model="showDeleteModal" title="Delete Connector" ok-variant="danger" @ok="deleteConnector">
      <p>Are you sure you want to permanently delete this connector? This action cannot be undone.</p>
    </b-modal>
  </div>
</template>

<script>
export default {
  props: {
    connectorId: { type: Number, required: true },
  },
  data() {
    return {
      saving: false,
      rotating: false,
      showRotateModal: false,
      showDeleteModal: false,
      newCredentials: null,
      form: {
        name: '',
        description: '',
        connection_mode: 'bidirectional',
        rate_limit_per_minute: 60,
        rate_limit_per_hour: 1000,
        rate_limit_per_day: 10000,
      },
      connectionModes: [
        { value: 'api', text: 'API Only' },
        { value: 'webhook', text: 'Webhook Only' },
        { value: 'bidirectional', text: 'Bidirectional (API + Webhook)' },
      ],
    }
  },
  computed: {
    connector() {
      return this.$store.state.appConnector.item || {}
    },
  },
  watch: {
    connector: {
      immediate: true,
      handler(val) {
        if (val && val.id) {
          this.form = {
            name: val.name || '',
            description: val.description || '',
            connection_mode: val.connection_mode || 'bidirectional',
            rate_limit_per_minute: val.rate_limit_per_minute || 60,
            rate_limit_per_hour: val.rate_limit_per_hour || 1000,
            rate_limit_per_day: val.rate_limit_per_day || 10000,
          }
        }
      },
    },
  },
  methods: {
    async updateConnector() {
      this.saving = true
      try {
        await this.$store.dispatch('appConnector/update', {
          id: this.connectorId,
          ...this.form,
        })
      } finally {
        this.saving = false
      }
    },
    async rotateCredentials() {
      this.rotating = true
      try {
        const data = await this.$store.dispatch('appConnector/regenerateCredentials', this.connectorId)
        this.newCredentials = data
        this.showRotateModal = false
      } finally {
        this.rotating = false
      }
    },
    async deleteConnector() {
      await this.$store.dispatch('appConnector/delete', this.connectorId)
      this.showDeleteModal = false
      this.$emit('deleted')
    },
    copyToClipboard(text) {
      if (text) navigator.clipboard.writeText(text)
    },
  },
}
</script>
